import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

interface StatItemProps {
  label: string;
  value: string | number;
  trend?: string;
  trendDirection?: 'up' | 'down';
  trendLabel?: string;
}

const StyledStatBox = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
}));

const VerticalDivider = styled(Box)(() => ({
  width: '1px',
  height: '48px',
  backgroundColor: '#ebebeb',
  flexShrink: 0,
}));

const StatItem: React.FC<StatItemProps> = ({ label, value, trend, trendDirection, trendLabel }) => {
  // Figma colors: success #1fc16b, error #fb3748, neutral text #5c5c5c
  const trendColor = trendDirection === 'up' ? '#1fc16b' : trendDirection === 'down' ? '#fb3748' : '#5c5c5c';

  return (
    <StyledStatBox>
      <Typography
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '16px',
          color: '#5c5c5c',
        }}
      >
        {label}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        spacing="6px"
        sx={{
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Inter Display", sans-serif',
            fontSize: '20px',
            fontWeight: 500,
            lineHeight: '28px',
            color: '#171717',
            flexShrink: 0,
          }}
        >
          {value}
        </Typography>
        {trend && (
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              color: trendColor,
              display: 'flex',
              alignItems: 'center',
              flex: '1 0 0',
              minWidth: 0,
            }}
          >
            {trend}
            {trendLabel && (
              <Box
                component="span"
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 500,
                  color: '#5c5c5c',
                  ml: 0,
                }}
              >
                {` ${trendLabel}`}
              </Box>
            )}
          </Typography>
        )}
      </Stack>
    </StyledStatBox>
  );
};

interface ProductHubStatsProps {
  stats: {
    totalProducts: number;
    uniqueCountries: number;
    uniqueHTS: number;
    avgTariffRate: number;
  };
}

const ProductHubStats: React.FC<ProductHubStatsProps> = ({ stats }) => {
  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #ebebeb',
        padding: '24px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '28px',
        }}
      >
        <StatItem
          label="Total Products"
          value={stats.totalProducts.toLocaleString()}
        />
        <VerticalDivider />
        <StatItem
          label="Countries"
          value={stats.uniqueCountries}
        />
        <VerticalDivider />
        <StatItem
          label="HTS Codes"
          value={stats.uniqueHTS}
        />
        <VerticalDivider />
        <StatItem
          label="Avg. Tariff Rate"
          value={`${stats.avgTariffRate.toFixed(1)}%`}
        />
      </Box>
    </Box>
  );
};

export default ProductHubStats;
